import { ProcessMessage } from './useProcessMessage'
import { AxiosInstance } from 'axios'
import { History } from 'history'
import { COMMAND_PREFIX } from '../../pages/Commands'
import { getEmailTimeSuggestionDescription, TimeSuggestionOverviewDto, TimeSuggestionStatus } from '../../pages/Time/TimeSuggestionOverview'
import { TrackTimeSuggestionDto } from '../../components/Dialog/TimeSuggestion/TimeSuggestionDialog'
import { useHistory } from 'react-router-dom'
import { Command, IBackgroundLocationState } from '../../config/appConfig'
import useUser from '../useUser'
import useAxios from '../../hooks/useAxios'
import { formatDifferenceInTime, parseDateTime } from '../../helpers/dateHelper'
import { useTranslation } from 'react-i18next'
import { useSettings } from '../useSettings'
import { LocalizedValueSetting, ModuleType, TimeSuggestionDescriptionOptionEnum } from '../../context/GlobalContext'
import useModules from '../module/useModules'
import useRoles, { rolesWriteAllTimes } from '../useRoles'

/**
 * Messages originating from the Inboxer in order to invoke a TimeSuggestionDialog.
 */
export type TimeTrackingNotification = ProcessMessage<{
    fileNumber: string
    timeSuggestionId: string
    date: string
    duration: string
    sender: string
    recipients: string
    emailInbound: string
    subject: string
}>

function showTimeSuggestionDialog(
    message: TimeTrackingNotification,
    axios: AxiosInstance,
    history: History,
    language: string,
    setting?: LocalizedValueSetting<string, LocalizedValueSetting<TimeSuggestionDescriptionOptionEnum>[]>[],
) {
    if (message.data?.timeSuggestionId === undefined) {
        return
    }

    axios
        .get<TimeSuggestionOverviewDto>(`/time-suggestion/${message.data.timeSuggestionId}`, {
            headers: { SuppressErrors: 'true', 'If-None-Match': null },
        })
        .then(response => {
            const timeSuggestion = response.data as TimeSuggestionOverviewDto
            // If we already tracked time for this email, skip popup.
            if (timeSuggestion.status !== TimeSuggestionStatus.ToReview) {
                return
            }

            const timeMutation: Partial<TrackTimeSuggestionDto> = {
                timeSuggestionId: message.data.timeSuggestionId,
                fileId: timeSuggestion.file.id,
                date: parseDateTime(timeSuggestion.emailSent).toFormat('yyyy-LL-dd'),
                duration: formatDifferenceInTime(timeSuggestion.emailCreated, timeSuggestion.emailSent, '00:01'),
                description: getEmailTimeSuggestionDescription(timeSuggestion, language, setting),
                emailInbound: timeSuggestion.emailInbound,
            }
            history.push(`${COMMAND_PREFIX}/${Command.TrackTime}`, { data: timeMutation })
        })
}

/**
 * Process a time-tracking event message. This invokes the TimeSuggestionDialog with custom initialValues.
 */
export default function useProcessTimeTrackingNotification() {
    const axios = useAxios()
    const { i18n } = useTranslation()
    const settings = useSettings()
    const modules = useModules()
    const roles = useRoles()

    const history = useHistory<IBackgroundLocationState>()
    const { currentUser } = useUser()

    return (message: TimeTrackingNotification) => {
        if (
            !modules.has(ModuleType.TimeTracking) ||
            !roles.any(rolesWriteAllTimes) ||
            currentUser?.useTimePopup === false
        ) {
            // Only push a time tracking popup to time tracking users that want a popup.
            return
        }

        showTimeSuggestionDialog(message, axios, history, i18n.language, settings.timeSuggestionDescription)
    }
}
